
import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { useNavigate } from "react-router"
import type { User } from "@/lib/types/database"
import { authApi } from "@/lib/api/auth"
import { usersApi, type ApiUserWithTeams } from "@/lib/api/users"
import { api, ApiError } from "@/lib/api/client"
import { mapApiUserToUser } from "@/lib/api/mappers"
import { queryClient } from "@/lib/query-client"
import {
  clearActiveOrg,
  clearOrgSnapshot,
  restoreActiveOrg,
  snapshotActiveOrg,
} from "@/lib/organization/storage"

// Impersonation is deliberately NOT durable across a browser restart.
//
// The token it installs lives in localStorage and the BE mints it for 30 days
// (auth.service.ts `generateToken`), so on its own it happily outlives the
// browser session. The marker saying "this identity is borrowed" used to live
// in sessionStorage, which dies with the browser — so reopening days later
// booted the app as the impersonated customer with no banner, no way back, and
// every action attributed to them.
//
// The flag therefore shares the token's tier (localStorage) and a separate
// one-bit witness in sessionStorage answers "are we still in the same browser
// session?". Flag present + witness gone == the browser was closed mid-
// impersonation, and the bootstrap effect below tears the session down.
//
// The witness is its own key rather than a reuse of `etx_original_token`: that
// one is only written when the admin already held a user token, which is *not*
// the case for the usual /admin/users flow (the admin console authenticates
// with an admin key, not an account), so its absence cannot distinguish a
// restart from an ordinary impersonation.
const IMPERSONATING_KEY = "etx_impersonating"
const IMPERSONATION_SESSION_KEY = "etx_impersonation_session"
const ORIGINAL_TOKEN_KEY = "etx_original_token"

// Surfaces reachable without an account. A torn-down impersonation must not
// yank someone off one of these — an admin checking the public consumer flow
// on the same browser would otherwise be bounced to the admin key prompt.
const PUBLIC_PATH_PREFIXES = [
  "/page/",
  "/track/",
  "/activate/",
  "/invites/",
  "/r/",
  "/schedule/",
]

function clearImpersonationKeys(): void {
  localStorage.removeItem(IMPERSONATING_KEY)
  // Legacy tier — a tab that began impersonating before the flag moved.
  sessionStorage.removeItem(IMPERSONATING_KEY)
  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY)
  sessionStorage.removeItem(ORIGINAL_TOKEN_KEY)
}

// Only an auth *rejection* should cost the user their session. A network
// error, a timeout (ApiError 408 from client.ts) or a 5xx says nothing about
// whether the token is still good — dropping it there silently logs out a
// valid session on a wake-from-sleep blip and strands the user on /login with
// no explanation.
function isAuthRejection(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403)
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isImpersonating: boolean
  hasTeams: boolean
  signIn: (email: string, password: string) => Promise<{ hasTeams: boolean }>
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ hasTeams: boolean }>
  signOut: () => Promise<void>
  impersonateAs: (token: string) => Promise<void>
  endImpersonation: () => void
  /** Re-fetches /auth/profile, updates context state, and returns the fresh
   *  ApiUserWithTeams so callers (e.g. invite.tsx) can read the just-joined
   *  team without waiting for React state to propagate. */
  refreshUser: () => Promise<ApiUserWithTeams>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasTeams, setHasTeams] = useState(false)
  const [isImpersonating, setIsImpersonating] = useState(
    () =>
      localStorage.getItem(IMPERSONATING_KEY) === "true" ||
      // Legacy: a tab already impersonating when this flag changed tiers.
      sessionStorage.getItem(IMPERSONATING_KEY) === "true",
  )
  const navigate = useNavigate()

  const loadUser = async (): Promise<ApiUserWithTeams> => {
    const apiUser = await usersApi.getMe()
    setUser(mapApiUserToUser(apiUser))
    setHasTeams(apiUser.teams.length > 0)
    return apiUser
  }

  // Install a token and drop every query cached under the previous identity.
  //
  // Query keys are scoped by team id, never by user (see `queryKeys.teams`),
  // so an identity switch inside one tab silently serves the *previous* user's
  // rows: `teams/members` still lists the admin, `useCurrentRole` finds no
  // matching email, returns null, and every role-gated route bounces to
  // /unauthorized — sticky until a full page reload, because nothing here
  // remounts. Clearing after `setToken` (never before, and never while the app
  // is tokenless) means any refetch the clear triggers goes out under the new
  // credentials rather than 401-ing into the hard `/login` redirect in
  // `client.ts`.
  const setIdentityToken = (token: string) => {
    api.setToken(token)
    queryClient.clear()
  }

  useEffect(() => {
    const token = localStorage.getItem("etx_access_token")
    if (!token) {
      setIsLoading(false)
      return
    }

    // Browser was closed mid-impersonation: the flag survived in localStorage,
    // the same-session witness did not. The token in hand is the impersonated
    // user's, so drop it rather than silently booting as them. The admin has no
    // account of their own, so /login would be a dead end — send them to the
    // admin key prompt instead.
    const impersonating = localStorage.getItem(IMPERSONATING_KEY) === "true"
    const sameSession = sessionStorage.getItem(IMPERSONATION_SESSION_KEY) !== null
    if (impersonating && !sameSession) {
      api.clearToken()
      clearActiveOrg()
      clearOrgSnapshot()
      clearImpersonationKeys()
      setIsImpersonating(false)
      setIsLoading(false)
      const path = window.location.pathname
      if (!PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) {
        navigate("/admin", { replace: true })
      }
      return
    }

    loadUser()
      .catch((err) => { if (isAuthRejection(err)) api.clearToken() })
      .finally(() => setIsLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const signIn = async (email: string, password: string) => {
    const { accessToken } = await authApi.signIn({ email, password })
    setIdentityToken(accessToken)
    const apiUser = await loadUser()
    return { hasTeams: apiUser.teams.length > 0 }
  }

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    const signupKey = localStorage.getItem("etx_signup_key") ?? undefined
    const { accessToken } = await authApi.signUp({ email, password, firstName, lastName }, signupKey)
    setIdentityToken(accessToken)
    const apiUser = await loadUser()
    return { hasTeams: apiUser.teams.length > 0 }
  }

  const signOut = async () => {
    setUser(null)
    setHasTeams(false)
    api.clearToken()
    clearImpersonationKeys()
    // Drop the impersonated user's active-team selection AND any lingering
    // admin snapshot — next login must start from a clean slate, otherwise
    // OrganizationContext re-hydrates a team the new user isn't a member of
    // and every team-scoped query 401s.
    clearActiveOrg()
    clearOrgSnapshot()
    // Deliberately no cache clear here: the app is tokenless from this point,
    // so a refetch would 401 into the hard `/login` redirect. The next
    // `setIdentityToken` (sign-in) clears it once a token is in place.
    setIsImpersonating(false)
  }

  const impersonateAs = async (token: string) => {
    const currentToken = localStorage.getItem("etx_access_token")
    if (currentToken) {
      sessionStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken)
    }
    // Stash admin's active-org keys to sessionStorage, then clear them so the
    // impersonated user's OrganizationContext init effect picks fresh defaults
    // rather than inheriting the admin's selection.
    snapshotActiveOrg()
    clearActiveOrg()
    setIdentityToken(token)
    localStorage.setItem(IMPERSONATING_KEY, "true")
    sessionStorage.setItem(IMPERSONATION_SESSION_KEY, "1")
    setIsImpersonating(true)
    await loadUser()
  }

  const endImpersonation = () => {
    const originalToken = sessionStorage.getItem(ORIGINAL_TOKEN_KEY)
    // Restore admin's active-org keys BEFORE loadUser() fires — the org init
    // effect re-reads localStorage when teams data lands, so the snapshot must
    // be back in place by then. Also clears the impersonated user's leftover
    // keys if no snapshot exists.
    if (originalToken) {
      restoreActiveOrg()
      // Drops the impersonated user's cached teams/members so the admin's own
      // role resolves against their rows, not the impersonated user's.
      setIdentityToken(originalToken)
    } else {
      // No way back to the admin session — fall through to a clean logout.
      api.clearToken()
      clearActiveOrg()
      clearOrgSnapshot()
    }
    clearImpersonationKeys()
    setIsImpersonating(false)
    setUser(null)
    setHasTeams(false)
    if (originalToken) {
      loadUser().catch((err) => { if (isAuthRejection(err)) api.clearToken() })
    }
  }

  const refreshUser = async () => {
    return loadUser()
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, isImpersonating, hasTeams, signIn, signUp, signOut, impersonateAs, endImpersonation, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
